import { ReactElement } from "react";
import { GetStaticProps } from "next";
import { NextSeo } from "next-seo";
import { MainLayout } from "~/components/layouts/MainLayout";
import { StandardServicePage } from "~/components/service-pages/StandardServicePage";
import { ElectricalSafetyCheckContent as content } from "~/content/przeglad-instalacji-elektrycznej/content.pl";
import type { City } from "~/lib/cities";
import { getCitiesWithServiceOffers } from "~/lib/firms.server";
import { getRecentPostsForService, type PostMeta } from "~/lib/mdx";
import { getServiceBySlug } from "~/lib/services";
import { NextPageWithLayout } from "~/pages/_app";
import electricalAssessmentImage from "~/src/assets/images/hero-power-box.webp";
import electricalMaintenanceImage from "~/src/assets/images/elec_11.jpg";
import electricalChecklistImage from "~/src/assets/images/services-details-electric.webp";

type Props = {
  recentPosts: PostMeta[];
  availableCities: City[];
};

const serviceSlug = "przeglad-instalacji-elektrycznej";
const service = getServiceBySlug(serviceSlug);

if (!service) {
  throw new Error(`Service not found: ${serviceSlug}`);
}

const pageUrl = `${(
  process.env.NEXT_PUBLIC_BASE_URL ?? "https://inspektomat.pl"
).replace(/\/+$/, "")}${service.href}`;

const sectionImages = [
  {
    src: electricalChecklistImage,
    alt: "Protokół z przeglądu instalacji elektrycznej",
  },
  {
    src: electricalMaintenanceImage,
    alt: "Kontrola stanu instalacji elektrycznej i pomiary ochronne",
  },
  {
    src: electricalAssessmentImage,
    alt: "Elektryk sprawdzający rozdzielnicę podczas przeglądu instalacji elektrycznej",
  },
] as const;

const ElectricalSafetyCheckPage: NextPageWithLayout<Props> = ({
  recentPosts,
  availableCities,
}) => {
  return (
    <StandardServicePage
      serviceSlug={serviceSlug}
      content={content}
      sectionImages={sectionImages}
      recentPosts={recentPosts}
      availableCities={availableCities}
    />
  );
};

ElectricalSafetyCheckPage.getLayout = function GetLayout(
  page: ReactElement
) {
  return (
    <MainLayout>
      <NextSeo
        title={content.seo.title}
        description={content.seo.description}
        openGraph={{
          title: content.seo.title,
          description: content.seo.description,
          url: pageUrl,
        }}
      />
      {page}
    </MainLayout>
  );
};

export default ElectricalSafetyCheckPage;

export const getStaticProps: GetStaticProps<Props> = async () => {
  return {
    props: {
      recentPosts: getRecentPostsForService(serviceSlug),
      availableCities: getCitiesWithServiceOffers(serviceSlug),
    },
  };
};
